import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import AutocompleteInput from '@/components/base/AutocompleteInput';
import ItemEntryTable from '@/pages/billing/components/ItemEntryTable';
import PrintProformaInvoice from '@/components/print/PrintProformaInvoice';
import { useToast } from '@/contexts/ToastContext';
import { emptyLineItem, calcTotals, type InvoiceLineItem } from '@/types/billing';
import { type MockProformaInvoice } from '@/mocks/billing';
import { proformaService } from '@/services/proformaService';
import { getCustomers } from '@/api/party.api';
import { getAllWarehouses, type WarehouseResponse } from '@/api/warehouse.api';
import { calcGST, isSameStateCheck, round2 } from '@/utils/gst';
import { formatINR } from '@/utils/format';
import { useWarehouseStore } from '@/stores/warehouseStore';
import DocumentNumberInput from '@/components/common/DocumentNumberInput';
import { getNextDocumentNumber } from '@/services/documentNumberService';
import { carryLineUnit } from '@/hooks/useUnits';

interface CustomerOption {
  id: string;
  name: string;
  type: string;
  gstin: string | null;
  phone: string | null;
  billingAddress: string;
  stateCode: string;
  isActive: boolean;
}

const COMPANY_STATE = '27';

const PAYMENT_TERMS_SUGGESTIONS = [
  '30 days net',
  '15 days net',
  'Advance',
  'COD',
  '45 days net',
  '7 days net',
  '50% advance, balance on delivery',
  '100% advance',
];
const DELIVERY_TERMS_SUGGESTIONS = ['Ex-Works', 'FOR Destination', 'CIF', 'FOB', 'Ex-Works Mumbai'];

interface QuotationPrefillState {
  quotationId: string;
  quotationNumber: string;
  customerId: string;
  customerName: string;
  customerGSTIN: string | null;
  warehouseId?: string | null;
  paymentTerms: string | null;
  deliveryTerms?: string | null;
  notes?: string | null;
  items: Array<{
    itemId?: string;
    itemCode?: string;
    itemName: string;
    hsnCode?: string;
    description?: string;
    qty: number;
    unit: string;
    unitId?: string;
    rate: number;
    discount: number;
    taxRate: number;
    enteredQty?: number | null;
    enteredUnitId?: string | null;
    enteredRate?: number | null;
  }>;
}

export default function ProformaNewPage() {
  const navigate = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const { selectedWarehouseId: topWarehouseId } = useWarehouseStore();

  const state = loc.state as Record<string, unknown> | null;
  const qtPrefill = (state as { fromQuotation?: QuotationPrefillState } | null)?.fromQuotation;
  const editMode = !!(state as { editMode?: boolean } | null)?.editMode;
  const editPiId = (state as { piId?: string } | null)?.piId;

  const [piNumber, setPiNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [validityDays, setValidityDays] = useState(30);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });

  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [warehouseId, setWarehouseId] = useState<string>(qtPrefill?.warehouseId ?? topWarehouseId ?? '');

  // Fetch next PI number dynamically based on warehouse prefix when creating new
  useEffect(() => {
    if (editMode) return;
    async function fetchNext() {
      try {
        const res = await getNextDocumentNumber('PI', warehouseId);
        if (res?.data?.nextNumber) {
          setPiNumber(res.data.nextNumber);
        }
      } catch (err) {
        console.warn('Failed to fetch next PI number:', err);
      }
    }
    fetchNext();
  }, [warehouseId, editMode]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(() => {
    if (qtPrefill) {
      return {
        id: qtPrefill.customerId || '',
        name: qtPrefill.customerName || '',
        type: 'CUSTOMER',
        gstin: qtPrefill.customerGSTIN || null,
        phone: null,
        billingAddress: '',
        stateCode: '27',
        isActive: true,
      };
    }
    return null;
  });
  const [customerQuery, setCustomerQuery] = useState(qtPrefill?.customerName ?? '');
  const [paymentTerms, setPaymentTerms] = useState(qtPrefill?.paymentTerms ?? '');
  const [deliveryTerms, setDeliveryTerms] = useState(qtPrefill?.deliveryTerms ?? '');
  const [expectedAdvance, setExpectedAdvance] = useState('');
  const [notes, setNotes] = useState(qtPrefill?.notes ?? '');
  const [bankDetails, setBankDetails] = useState('');
  const [showPaymentSuggestions, setShowPaymentSuggestions] = useState(false);
  const [showDeliverySuggestions, setShowDeliverySuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [savedProforma, setSavedProforma] = useState<MockProformaInvoice | null>(null);
  const [fromQuotationId, setFromQuotationId] = useState<string | null>(qtPrefill?.quotationId ?? null);
  const [fromQuotationNumber, setFromQuotationNumber] = useState<string | null>(qtPrefill?.quotationNumber ?? null);
  const [fromQtMode, setFromQtMode] = useState(!!qtPrefill);

  const [items, setItems] = useState<InvoiceLineItem[]>(() => {
    if (qtPrefill && qtPrefill.items.length > 0) {
      return qtPrefill.items.map((qi, idx) => {
        // Carried over in the unit the quotation was made in
        const carried = carryLineUnit(qi);
        const qty = carried.qty || 1;
        const rate = carried.rate || 0;
        const discount = qi.discount || 0;
        const taxRate = qi.taxRate || 18;
        const taxable = round2(qty * rate * (1 - discount / 100));
        const gst = calcGST(taxRate, taxable, true);
        return {
          ...emptyLineItem(),
          id: `pi-item-${idx}`,
          itemId: qi.itemId || '',
          itemCode: qi.itemCode || '',
          itemName: qi.itemName,
          hsnCode: qi.hsnCode || '',
          narration: qi.description || '',
          qty,
          unit: qi.unit || 'Pcs',
          unitId: qi.unitId || '',
          enteredUnitId: carried.enteredUnitId,
          rate,
          discount,
          taxRate,
          taxableAmount: taxable,
          ...gst,
          total: round2(taxable + gst.cgst + gst.sgst + gst.igst),
        };
      });
    }
    return [emptyLineItem()];
  });

  // Load Warehouses dynamically from backend
  useEffect(() => {
    async function loadWarehouses() {
      try {
        const res = await getAllWarehouses();
        if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
          const list = res.data.map((w: WarehouseResponse) => ({ id: w.id, name: w.name }));
          setWarehouses(list);
          if (qtPrefill?.warehouseId && list.some((w) => w.id === qtPrefill.warehouseId)) {
            setWarehouseId(qtPrefill.warehouseId);
          } else if (topWarehouseId && list.some((w) => w.id === topWarehouseId)) {
            setWarehouseId(topWarehouseId);
          } else if (list.length > 0) {
            setWarehouseId((prev) => (prev && list.some((w) => w.id === prev) ? prev : list[0].id));
          }
        } else {
          setWarehouses([]);
          setWarehouseId('');
        }
      } catch (err) {
        console.error('Failed to fetch warehouses:', err);
        setWarehouses([]);
        setWarehouseId('');
      }
    }
    loadWarehouses();
  }, [qtPrefill?.warehouseId, topWarehouseId]);

  // Pre-fetch customer detail if creating from quotation
  useEffect(() => {
    if (qtPrefill && qtPrefill.customerName) {
      getCustomers({ search: qtPrefill.customerName, isActive: true })
        .then((res) => {
          if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
            const match =
              res.data.find(
                (p: any) =>
                  p.id === qtPrefill.customerId ||
                  p.name.toLowerCase() === qtPrefill.customerName.toLowerCase()
              ) || res.data[0];

            if (match) {
              setSelectedCustomer({
                id: match.id,
                name: match.name,
                type: 'CUSTOMER',
                gstin: match.gstin || qtPrefill.customerGSTIN || null,
                phone: match.phone || match.mobile || null,
                billingAddress: match.billing_address || match.billingAddress || '',
                stateCode: match.state_code || match.stateCode || '27',
                isActive: match.is_active ?? true,
              });
              setCustomerQuery(match.name);
            }
          }
        })
        .catch((err) => console.warn('Failed to pre-fetch customer for prefill:', err));
    }
  }, [qtPrefill?.customerId, qtPrefill?.customerName]);

  // Fetch PI detail if editMode
  useEffect(() => {
    if (editMode && editPiId) {
      proformaService
        .getProformaInvoice(editPiId)
        .then((pi) => {
          if (pi) {
            if (pi.piNumber || (pi as any).pi_number) setPiNumber(pi.piNumber || (pi as any).pi_number);
            setDate(pi.date);
            setValidUntil(pi.validUntil);
            setValidityDays(pi.validityDays);
            setCustomerQuery(pi.customerName);
            setSelectedCustomer({
              id: pi.customerId,
              name: pi.customerName,
              type: 'CUSTOMER',
              gstin: pi.customerGSTIN || null,
              phone: pi.customerPhone || null,
              billingAddress: pi.billingAddress || '',
              stateCode: '27',
              isActive: true,
            });
            if (pi.warehouseId) setWarehouseId(pi.warehouseId);
            setPaymentTerms(pi.paymentTerms || '');
            setDeliveryTerms(pi.deliveryTerms || '');
            setExpectedAdvance(pi.expectedAdvance ? String(pi.expectedAdvance) : '');
            setNotes(pi.notes || '');
            setBankDetails(pi.bankDetails || '');
            setFromQuotationId(pi.fromQuotationId);
            setFromQuotationNumber(pi.fromQuotationNumber);

            if (pi.items && pi.items.length > 0) {
              setItems(
                pi.items.map((i, idx) => ({
                  ...emptyLineItem(),
                  id: `pi-item-${idx}`,
                  itemId: i.itemId,
                  itemName: i.itemName,
                  hsnCode: i.hsnCode,
                  // As entered when another unit than the stock unit was used
                  ...carryLineUnit(i as Parameters<typeof carryLineUnit>[0]),
                  unit: i.unit,
                  discount: i.discountPercent || 0,
                  taxRate: i.taxRate || 18,
                  taxableAmount: i.taxableAmount,
                  cgst: i.cgst,
                  sgst: i.sgst,
                  igst: i.igst,
                  total: i.totalAmount,
                }))
              );
            }
          }
        })
        .catch((e) => console.error('Failed to fetch PI detail for edit:', e));
    }
  }, [editMode, editPiId]);

  const totals = useMemo(() => calcTotals(items), [items]);
  const isSameState = isSameStateCheck(selectedCustomer?.stateCode ?? '', COMPANY_STATE);

  const cgstTotal = items.reduce(
    (s, i) => s + (isSameState ? round2((i.qty * i.rate * (1 - i.discount / 100)) * (i.taxRate / 200)) : 0),
    0
  );
  const sgstTotal = items.reduce(
    (s, i) => s + (isSameState ? round2((i.qty * i.rate * (1 - i.discount / 100)) * (i.taxRate / 200)) : 0),
    0
  );
  const igstTotal = items.reduce(
    (s, i) => s + (!isSameState ? round2((i.qty * i.rate * (1 - i.discount / 100)) * (i.taxRate / 100)) : 0),
    0
  );
  const taxTotal = cgstTotal + sgstTotal + igstTotal;
  const discountTotal = totals.subtotal - totals.taxableAmount;
  const grandTotal = round2(totals.taxableAmount + taxTotal);

  // Fetch customers dynamically from API strictly for selected warehouse
  const fetchParties = useCallback(
    async (q: string): Promise<CustomerOption[]> => {
      try {
        const res = await getCustomers({ search: q, warehouse_id: warehouseId || undefined, isActive: true });
        if (res && res.data && Array.isArray(res.data)) {
          return res.data.map((p: any) => ({
            id: p.id,
            name: p.name,
            type: 'CUSTOMER',
            gstin: p.gstin || null,
            phone: p.phone || p.mobile || null,
            billingAddress: p.billing_address || p.billingAddress || '',
            stateCode: p.state_code || p.stateCode || '',
            isActive: p.is_active ?? true,
          }));
        }
      } catch (e) {
        console.error('Customer API search failed:', e);
      }
      return [];
    },
    [warehouseId]
  );

  const handleCustomerSelect = useCallback((p: CustomerOption) => {
    setSelectedCustomer(p);
    setCustomerQuery(p.name);
  }, []);

  const handleWarehouseChange = (wId: string) => {
    setWarehouseId(wId);
    setSelectedCustomer(null);
    setCustomerQuery('');
  };

  const handleValidityDaysChange = (days: number) => {
    setValidityDays(days);
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setValidUntil(d.toISOString().split('T')[0]);
  };

  const handleValidUntilChange = (vu: string) => {
    setValidUntil(vu);
    const d1 = new Date(date);
    const d2 = new Date(vu);
    const diff = Math.round((d2.getTime() - d1.getTime()) / 86400000);
    if (diff > 0) setValidityDays(diff);
  };

  const handleDateChange = (dt: string) => {
    setDate(dt);
    const d = new Date(dt);
    d.setDate(d.getDate() + validityDays);
    setValidUntil(d.toISOString().split('T')[0]);
  };

  const buildProformaData = (status: 'DRAFT' | 'SENT'): MockProformaInvoice => {
    const validItems = items.filter((i) => i.itemName.trim() && i.qty > 0);
    const expAdv = parseFloat(expectedAdvance) || 0;

    return {
      id: `pi-${Date.now()}`,
      piNumber: 'PI-PENDING',
      date,
      validUntil,
      validityDays,
      customerId: selectedCustomer?.id || '',
      customerName: selectedCustomer?.name || customerQuery,
      customerGSTIN: selectedCustomer?.gstin || undefined,
      customerPhone: selectedCustomer?.phone || undefined,
      customerEmail: null,
      warehouseId,
      status,
      fromQuotationId,
      fromQuotationNumber,
      convertedToInvoiceId: null,
      convertedToInvoiceNumber: null,
      items: validItems.map((i, idx) => ({
        id: `pii-${idx + 1}`,
        itemId: i.itemId,
        itemName: i.itemName,
        hsnCode: i.hsnCode,
        description: i.description || null,
        qty: i.qty,
        unit: i.unit || 'Pcs',
        rate: i.rate,
        discountPercent: i.discount,
        discountAmount: round2(i.qty * i.rate * (i.discount / 100)),
        taxableAmount: i.taxableAmount,
        taxRate: i.taxRate,
        cgst: i.cgst,
        sgst: i.sgst,
        igst: i.igst,
        totalAmount: i.total,
      })),
      subtotal: totals.subtotal,
      discountAmount: discountTotal,
      taxableAmount: totals.taxableAmount,
      cgst: cgstTotal,
      sgst: sgstTotal,
      igst: igstTotal,
      taxAmount: taxTotal,
      totalAmount: grandTotal,
      expectedAdvance: expAdv,
      balanceDue: Math.max(0, grandTotal - expAdv),
      notes: notes || null,
      paymentTerms: paymentTerms || null,
      deliveryTerms: deliveryTerms || null,
      bankDetails: bankDetails || null,
      preparedBy: 'Admin User',
      createdAt: new Date().toISOString(),
    };
  };

  // Check if quotation is already converted to PI
  useEffect(() => {
    if (qtPrefill?.quotationId) {
      proformaService
        .listProformaInvoices()
        .then((res) => {
          if (Array.isArray(res)) {
            const existing = res.find((pi: any) => pi.fromQuotationId === qtPrefill.quotationId);
            if (existing) {
              toast.info(`Quotation ${qtPrefill.quotationNumber || ''} is already converted to Proforma Invoice ${existing.piNumber || ''}`);
              navigate(`/sales/proforma/${existing.id}`, { replace: true });
            }
          }
        })
        .catch(() => { });
    }
  }, [qtPrefill?.quotationId, qtPrefill?.quotationNumber, navigate, toast]);

  const handleSave = async (status: 'DRAFT' | 'SENT') => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    const validItems = items.filter((i) => i.itemName.trim() && i.qty > 0 && i.rate > 0);
    if (validItems.length === 0) {
      toast.error('Add at least one item with name, qty > 0, and rate > 0');
      return;
    }
    setSaving(true);

    try {
      const expAdv = parseFloat(expectedAdvance) || 0;
      const payload = {
        piNumber,
        customerId: selectedCustomer?.id || '',
        customerName: selectedCustomer?.name || customerQuery,
        customerGSTIN: selectedCustomer?.gstin || undefined,
        customerPhone: selectedCustomer?.phone || undefined,
        warehouseId: warehouseId || undefined,
        date,
        validUntil,
        validityDays,
        status,
        fromQuotationId: fromQuotationId || undefined,
        fromQuotationNumber: fromQuotationNumber || undefined,
        subtotal: totals.subtotal,
        discountAmount: discountTotal,
        taxableAmount: totals.taxableAmount,
        cgst: cgstTotal,
        sgst: sgstTotal,
        igst: igstTotal,
        taxAmount: taxTotal,
        totalAmount: grandTotal,
        expectedAdvance: expAdv,
        balanceDue: Math.max(0, grandTotal - expAdv),
        notes: notes || undefined,
        paymentTerms: paymentTerms || undefined,
        deliveryTerms: deliveryTerms || undefined,
        bankDetails: bankDetails || undefined,
        preparedBy: 'Admin User',
        items: validItems.map((i) => ({
          itemId: i.itemId,
          itemName: i.itemName,
          hsnCode: i.hsnCode,
          description: i.description,
          qty: i.qty,
          unit: i.unit,
          // qty and rate are in this unit; the server converts them to the stock unit
          enteredUnitId: i.enteredUnitId || undefined,
          rate: i.rate,
          discountPercent: i.discount,
          discountAmount: round2(i.qty * i.rate * (i.discount / 100)),
          taxableAmount: i.taxableAmount,
          taxRate: i.taxRate,
          cgst: i.cgst,
          sgst: i.sgst,
          igst: i.igst,
          totalAmount: i.total,
        })),
      };

      let result;
      if (editMode && editPiId) {
        result = await proformaService.updateProformaInvoice(editPiId, payload);
      } else {
        result = await proformaService.createProformaInvoice(payload);
      }
      const newPiId = result?.id || editPiId || `pi-${Date.now()}`;
      const newPiNum = result?.piNumber || 'PI-SAVED';

      setSavedProforma({
        ...buildProformaData(status),
        id: newPiId,
        piNumber: newPiNum,
      });

      // Update Quotation status to CONVERTED_TO_PI if created from quotation
      const activeQuotId = fromQuotationId || qtPrefill?.quotationId;
      if (activeQuotId) {
        try {
          const { quotationService } = await import('@/services/quotationService');
          await quotationService.updateStatus(activeQuotId, 'CONVERTED_TO_PI', {
            convertedToPIId: newPiId,
            convertedToPINumber: newPiNum,
          });
        } catch (e) {
          console.warn('Failed to update quotation status:', e);
        }
      }

      if (status === 'SENT') {
        toast.success(`Proforma ${newPiNum} saved and marked as sent`);
      } else {
        toast.success(`Proforma ${newPiNum} saved as draft`);
      }
      navigate('/sales/proforma');
    } catch (err: any) {
      console.error('Save proforma error:', err);
      toast.error(err.message || 'Failed to save proforma invoice');
    } finally {
      setSaving(false);
    }
  };

  const getPrintData = () => savedProforma || buildProformaData('DRAFT');

  const fl =
    'w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700';
  const lb = 'block text-xs font-semibold text-slate-500 mb-1';

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto p-6 pb-16">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-lg font-bold text-slate-800">
                {editMode ? 'Edit Proforma Invoice' : 'New Proforma Invoice'}
              </h1>
              <p className="text-sm text-slate-500">Create advance estimation invoice</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSave('DRAFT')}
                disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-draft-line text-xs" /> Save as Draft
              </button>
              <button
                onClick={() => handleSave('SENT')}
                disabled={saving}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" /> Save &amp; Send
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quotation Banner */}
          {fromQtMode && fromQuotationNumber && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-2.5">
              <i className="ri-link text-teal-600" />
              <span className="text-sm font-medium text-teal-700">
                Creating Proforma from {fromQuotationNumber}
              </span>
              <button
                onClick={() => {
                  setFromQtMode(false);
                  setFromQuotationId(null);
                  setFromQuotationNumber(null);
                }}
                className="ml-auto text-xs text-teal-500 hover:text-red-500 cursor-pointer transition-colors"
              >
                Unlink
              </button>
            </div>
          )}

          {/* Form: Header fields */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Proforma Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className={lb}>Customer <span className="text-red-500">*</span></label>
                <AutocompleteInput<CustomerOption>
                  value={customerQuery}
                  onChange={setCustomerQuery}
                  onSelect={handleCustomerSelect}
                  fetchOptions={fetchParties}
                  placeholder="Search customer for selected warehouse..."
                  renderOption={(p) => (
                    <div>
                      <span className="font-medium">{p.name}</span>
                      {p.gstin && <span className="text-xs text-slate-400 ml-2">{p.gstin}</span>}
                    </div>
                  )}
                  getOptionKey={(p) => p.id}
                  getOptionLabel={(p) => p.name}
                  minChars={1}
                />
              </div>

              <div>
                <label className={lb}>PI Date</label>
                <input type="date" value={date} onChange={(e) => handleDateChange(e.target.value)} className={fl} />
              </div>

              <div>
                <label className={lb}>Valid Until</label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => handleValidUntilChange(e.target.value)}
                  className={fl}
                />
              </div>

              <div>
                <label className={lb}>Validity (Days)</label>
                <input
                  type="number"
                  value={validityDays}
                  onChange={(e) => handleValidityDaysChange(Number(e.target.value) || 1)}
                  min={1}
                  max={365}
                  className={fl}
                />
              </div>

              <div className="relative">
                <label className={lb}>Payment Terms</label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  onFocus={() => setShowPaymentSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowPaymentSuggestions(false), 200)}
                  className={fl}
                  placeholder="e.g. 50% advance, balance on delivery"
                />
                {showPaymentSuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                    {PAYMENT_TERMS_SUGGESTIONS.filter((s) =>
                      s.toLowerCase().includes(paymentTerms.toLowerCase())
                    ).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={() => {
                          setPaymentTerms(s);
                          setShowPaymentSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <label className={lb}>Delivery Terms</label>
                <input
                  type="text"
                  value={deliveryTerms}
                  onChange={(e) => setDeliveryTerms(e.target.value)}
                  onFocus={() => setShowDeliverySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowDeliverySuggestions(false), 200)}
                  className={fl}
                  placeholder="e.g. Ex-Works, FOR Destination"
                />
                {showDeliverySuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                    {DELIVERY_TERMS_SUGGESTIONS.filter((s) =>
                      s.toLowerCase().includes(deliveryTerms.toLowerCase())
                    ).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={() => {
                          setDeliveryTerms(s);
                          setShowDeliverySuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedCustomer && (
              <div className="mt-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg text-xs text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
                <span>
                  Customer: <strong>{selectedCustomer.name}</strong>
                </span>
                {selectedCustomer.gstin && (
                  <span>
                    GSTIN: <strong>{selectedCustomer.gstin}</strong>
                  </span>
                )}
                {selectedCustomer.phone && (
                  <span>
                    Phone: <strong>{selectedCustomer.phone}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Expected Advance & Bank Details */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Advance &amp; Bank Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={lb}>Expected Advance (₹)</label>
                <input
                  type="number"
                  value={expectedAdvance}
                  onChange={(e) => setExpectedAdvance(e.target.value)}
                  placeholder="e.g. 100000"
                  className={fl}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Enter expected advance amount from customer. Informational only — actual payment recorded after invoice.
                </p>
              </div>
              <div>
                <label className={lb}>Bank Details</label>
                <textarea
                  value={bankDetails}
                  onChange={(e) => setBankDetails(e.target.value)}
                  className={`${fl} h-24 resize-none`}
                  placeholder="Bank account details for advance payment..."
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <label className={lb}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${fl} h-20 resize-none`}
              placeholder="Proforma notes..."
            />
          </div>

          {/* Items Table */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Items</h2>
              <span className="text-xs text-slate-400">{items.filter((i) => i.itemName.trim()).length} items</span>
            </div>
            <ItemEntryTable
              items={items}
              onChange={setItems}
              isSameState={isSameState}
              warehouseId={warehouseId}
              rateType="sale"
            />
          </div>

          {/* Proforma Info & Summary Panel */}
          <div className="bg-white border border-slate-200 p-5 mt-4 max-w-sm ml-auto rounded-xl shadow-xs mb-6">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Proforma Info &amp; Summary</h2>

            <div className="mb-3">
              <DocumentNumberInput
                value={piNumber}
                onChange={setPiNumber}
                label="Proforma Invoice Number"
              />
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold border border-teal-200">
                VALID: {validityDays} DAYS
              </span>
            </div>

            <div className="space-y-2 text-sm border-t border-slate-100 pt-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-medium text-slate-700">{formatINR(totals.subtotal)}</span>
              </div>
              {discountTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Discount</span>
                  <span className="font-medium text-red-600">-{formatINR(discountTotal)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Taxable Amount</span>
                <span className="font-medium text-slate-700">{formatINR(totals.taxableAmount)}</span>
              </div>
              {isSameState ? (
                <>
                  {cgstTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">CGST</span>
                      <span className="font-medium text-slate-700">{formatINR(cgstTotal)}</span>
                    </div>
                  )}
                  {sgstTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">SGST</span>
                      <span className="font-medium text-slate-700">{formatINR(sgstTotal)}</span>
                    </div>
                  )}
                </>
              ) : (
                igstTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">IGST</span>
                    <span className="font-medium text-slate-700">{formatINR(igstTotal)}</span>
                  </div>
                )
              )}
              <div className="border-t border-slate-200 pt-2 flex justify-between">
                <span className="font-bold text-slate-800">Grand Total</span>
                <span className="font-bold text-indigo-600 text-base">{formatINR(grandTotal)}</span>
              </div>
              <div className="text-xs text-slate-400">
                {items.filter((i) => i.itemName.trim()).length} items
              </div>
            </div>
          </div>
        </div>

        {/* Print Preview */}
        {printOpen && <PrintProformaInvoice proforma={getPrintData()} onClose={() => setPrintOpen(false)} />}
      </div>
    </AppLayout>
  );
}